// ============================================================
// reportData.ts — Real data layer for the Symptom Intelligence Report.
//
// One source of truth for:
//   - Tracking-since date (earliest of first daily_log or signupDate)
//   - Milestone day = floor((today_local - trackingSince) / 1d) + 1
//   - Logged-days-in-last-90 count (distinct dates with ≥1 symptom log)
//   - Per-symptom severity, indexed by local-TZ date key
//   - Last-30 vs prior-30 windows for Symptom Direction
//
// All date math uses localDateKey() so late-evening logs don't slip
// across a UTC day boundary.
// ============================================================
import { supabase } from "@/integrations/supabase/client";
import { localDateKey, fetchLogsBetween } from "./dailyLogs";

// Symptoms that the PDF's Direction table / Heatmap / Trends chart
// reason about. Subset of the in-app symList.
// Severity-tracked symptoms the PDF's Direction table / Daily Averages /
// flags reason about. "Hot flashes" intentionally EXCLUDED — it's event-
// tracked (count + occurred_at via symptom_events) and rendered in its own
// PDF section. Including it here would re-introduce the stale-severity bug
// the Turn-2e migration fixed.
export const REPORT_SYMPTOMS = [
  "Night sweats",
  "Brain fog",
  "Mood swings",
  "Fatigue",
  "Poor sleep",
] as const;

export type ReportSymptom = (typeof REPORT_SYMPTOMS)[number];

export type ReportData = {
  /** Earliest YYYY-MM-DD we consider the user to be "tracking since". */
  trackingSinceKey: string | null;
  /** Human-readable label, e.g. "April 14, 2026". */
  trackingSinceLabel: string;
  /** floor((today_local - trackingSinceKey) / day) + 1, or 1 if unknown. */
  milestoneDay: number;
  /** Distinct local dates in the last 90 days that have ≥1 symptom row. */
  loggedDays90: number;
  /** YYYY-MM-DD keys (oldest → newest), last 90 days only. */
  loggedDates90: string[];
  /**
   * dateKey → symptomName → severity (raw 1..4). Only dates with at
   * least one logged symptom appear. Missing dates = "no log that day".
   * Spans the last 90 days.
   */
  logsByDate: Record<string, Record<string, number>>;
};

export type DirectionRow = {
  name: ReportSymptom | string;
  /** Average severity on the 1..4 scale across logged days in last-30. */
  lastAvg: number;
  /** Average severity on the 1..4 scale across logged days in prior-30. */
  priorAvg: number;
  /** Distinct logged days in last-30 that contained this symptom. */
  lastDays: number;
  priorDays: number;
  /** Days in last-30 where severity ≥ 3. */
  lastSevere: number;
  /** % change from priorAvg → lastAvg, integer. 0 if priorAvg is 0. */
  pct: number;
  /** Bucketed direction. */
  dir: "improving" | "worsening" | "stable";
};

export type DirectionWindow = {
  directions: DirectionRow[];
  /** Distinct local dates in last-30 with ≥1 log. */
  loggedLast30: number;
  /** Distinct local dates in prior-30 with ≥1 log. */
  loggedPrior30: number;
};

function emptyReportData(user: any): ReportData {
  const signupKey = user?.signupDate
    ? localDateKey(new Date(user.signupDate))
    : null;
  let milestoneDay = 1;
  if (signupKey) {
    const todayKey = localDateKey();
    const ms =
      (new Date(todayKey + "T00:00:00").getTime() -
        new Date(signupKey + "T00:00:00").getTime()) /
      86400000;
    milestoneDay = Math.max(1, Math.floor(ms) + 1);
  }
  return {
    trackingSinceKey: signupKey,
    trackingSinceLabel: signupKey ? fmtLabel(signupKey) : "—",
    milestoneDay,
    loggedDays90: 0,
    loggedDates90: [],
    logsByDate: {},
  };
}

function fmtLabel(key: string): string {
  return new Date(key + "T00:00:00").toLocaleDateString("en-US", {
    month: "long",
    day: "numeric",
    year: "numeric",
  });
}

/**
 * Returns the date key N days before `today` (local TZ).
 * daysAgoKey(0) === today's local key.
 */
function daysAgoKey(daysAgo: number): string {
  const d = new Date();
  d.setDate(d.getDate() - daysAgo);
  return localDateKey(d);
}

/**
 * Fetches everything the Symptom Intelligence Report needs from real tables.
 * Falls back gracefully if the user is signed out or has no logs.
 */
export async function fetchReportData(user: any): Promise<ReportData> {
  const { data: authData } = await supabase.auth.getUser();
  const userId = authData?.user?.id;
  if (!userId) return emptyReportData(user);

  const todayKey = daysAgoKey(0);
  const start90Key = daysAgoKey(89);

  // Last-90 logs.
  let logs: { log_date: string; symptom_name: string; severity: number }[] = [];
  try {
    logs = await fetchLogsBetween(userId, start90Key, todayKey);
  } catch (e) {
    console.warn("[reportData] fetchLogsBetween failed", e);
  }

  // Earliest log overall (may be older than 90d).
  let earliestLogDate: string | null = null;
  try {
    const { data: earliestRow } = await supabase
      .from("daily_logs")
      .select("log_date")
      .eq("user_id", userId)
      .order("log_date", { ascending: true })
      .limit(1)
      .maybeSingle();
    earliestLogDate = (earliestRow?.log_date as string) ?? null;
  } catch (e) {
    console.warn("[reportData] earliest log lookup failed", e);
  }

  const signupKey = user?.signupDate
    ? localDateKey(new Date(user.signupDate))
    : null;
  const candidates = [earliestLogDate, signupKey].filter(Boolean) as string[];
  candidates.sort();
  const trackingSinceKey = candidates[0] ?? null;

  let milestoneDay = 1;
  if (trackingSinceKey) {
    const ms =
      (new Date(todayKey + "T00:00:00").getTime() -
        new Date(trackingSinceKey + "T00:00:00").getTime()) /
      86400000;
    milestoneDay = Math.max(1, Math.floor(ms) + 1);
  }

  const logsByDate: Record<string, Record<string, number>> = {};
  const dateSet = new Set<string>();
  for (const r of logs) {
    if (!logsByDate[r.log_date]) logsByDate[r.log_date] = {};
    logsByDate[r.log_date][r.symptom_name] = r.severity;
    dateSet.add(r.log_date);
  }
  const loggedDates90 = [...dateSet].sort();

  return {
    trackingSinceKey,
    trackingSinceLabel: trackingSinceKey ? fmtLabel(trackingSinceKey) : "—",
    milestoneDay,
    loggedDays90: loggedDates90.length,
    loggedDates90,
    logsByDate,
  };
}

/**
 * Builds last-30 vs prior-30 averages per symptom from real logs.
 * Averages are on the displayed 1..4 severity scale (no offset needed —
 * daily_logs.severity is already stored on the 1..4 scale).
 *
 * `names` defaults to REPORT_SYMPTOMS so every existing caller (peri/meno
 * Doctor Report, in-app Symptom Intelligence card, card preview) is
 * byte-identical. The postmeno Annual Wellness Summary passes the full set
 * of names actually logged in the 60-day window so Anxiety / Headaches /
 * Joint pain etc. are not silently dropped. Single source of direction
 * math — never clone this function.
 */
/**
 * Returns the set of severity-tracked symptoms the patient has actually
 * logged in the last 60 days (the union of the last-30 + prior-30 windows
 * used by Symptom Direction). "Hot flashes" is intentionally excluded —
 * it's event-tracked and rendered in its own section. Used as the universe
 * for the peri Doctor Report so every consumer (Symptom Direction table,
 * Clinical Flags, Trends chart, Discussion Points, in-app preview) reasons
 * about the patient's real symptoms rather than the hardcoded default.
 */
export function loggedSymptomsForDirections(data: ReportData): string[] {
  const keys: string[] = [];
  for (let i = 0; i < 60; i++) keys.push(daysAgoKey(i));
  const s = new Set<string>();
  for (const k of keys) {
    const row = data.logsByDate[k];
    if (!row) continue;
    for (const name of Object.keys(row)) {
      if (name === "Hot flashes") continue;
      if (typeof row[name] === "number") s.add(name);
    }
  }
  return [...s].sort();
}

export function buildSymptomDirections(
  data: ReportData,
  names: readonly string[] = REPORT_SYMPTOMS,
): DirectionWindow {
  const last30Keys: string[] = [];
  const prior30Keys: string[] = [];
  for (let i = 0; i < 30; i++) last30Keys.push(daysAgoKey(i));
  for (let i = 30; i < 60; i++) prior30Keys.push(daysAgoKey(i));

  const loggedLast30 = last30Keys.filter((k) => data.logsByDate[k]).length;
  const loggedPrior30 = prior30Keys.filter((k) => data.logsByDate[k]).length;

  const directions: DirectionRow[] = names.map((name) => {

    const lastVals: number[] = [];
    const priorVals: number[] = [];
    for (const k of last30Keys) {
      const v = data.logsByDate[k]?.[name];
      if (typeof v === "number") lastVals.push(v);
    }
    for (const k of prior30Keys) {
      const v = data.logsByDate[k]?.[name];
      if (typeof v === "number") priorVals.push(v);
    }
    const lastAvg = lastVals.length
      ? lastVals.reduce((s, v) => s + v, 0) / lastVals.length
      : 0;
    const priorAvg = priorVals.length
      ? priorVals.reduce((s, v) => s + v, 0) / priorVals.length
      : 0;
    const lastSevere = lastVals.filter((v) => v >= 3).length;
    const pct =
      priorAvg > 0
        ? Math.round(((lastAvg - priorAvg) / priorAvg) * 100)
        : 0;
    const dir: DirectionRow["dir"] =
      pct < -15 ? "improving" : pct > 15 ? "worsening" : "stable";
    return {
      name,
      lastAvg,
      priorAvg,
      lastDays: lastVals.length,
      priorDays: priorVals.length,
      lastSevere,
      pct,
      dir,
    };
  });

  return { directions, loggedLast30, loggedPrior30 };
}

// ============================================================
// SECTION C — HRT Response (30 days before vs 30 days after)
// ============================================================
import { parseLocalDateKey } from "./dailyLogs";

export type HrtResponseRow = {
  symptom: string;
  preAvg: number;
  postAvg: number;
  delta: number;       // postAvg - preAvg, signed
  preN: number;        // logs in pre window
  postN: number;       // logs in post window
  direction: "↓" | "→" | "↑";
};

export type HrtResponse = {
  state: "hidden" | "pending" | "data-floor" | "ready";
  /** Only set when state === 'pending'. Days until hrtStartDate + 30. */
  daysUntilUnlock: number | null;
  /** Only set when state === 'data-floor'. Distinct logged days in pre window. */
  preDays: number | null;
  /** Only set when state === 'data-floor'. Distinct logged days in post window. */
  postDays: number | null;
  /** Only set when state === 'ready'. Sorted by delta ascending. */
  rows: HrtResponseRow[] | null;
  totalImproved: number;
  totalUnchanged: number;
  totalWorsened: number;
  /**
   * Only meaningful when state === 'ready'. Overall symptom-load change as
   * an integer percent on the 1–4 severity scale.
   * Sign convention: positive = symptom load REDUCED after HRT (improvement);
   * negative = symptom load INCREASED after HRT.
   */
  overallPct: number;
  /**
   * Only set when state === 'ready' and at least one row qualified. The
   * symptom with the smallest per-symptom improvement (most worsened, or
   * least improved). `pct` uses the same sign convention as overallPct:
   * positive = improved, negative = worsened.
   */
   worstResponder: { name: string; pct: number } | null;
  /**
   * Only set when state === 'ready' and at least one row qualified. The
   * symptom with the largest improvement (most reduced after HRT). Same
   * sign convention as worstResponder: positive = improved.
   */
  topImprover: { name: string; pct: number } | null;
  /**
   * Only set when state === 'ready'. 60-day timeseries used by the in-app
   * HRT before/after chart: 30 days pre-start + 30 days post-start (start
   * day excluded). `total` = sum of severities across qualifying symptoms
   * for that day (0 if no logs). `avg` = 7-day centered rolling avg of
   * total. `date` = "Mon D" label matching other chart axes.
   */
  chartData: { date: string; total: number; phase: "before" | "after"; avg: number }[] | null;
  /** "Mon D" label of the HRT start day (used for the chart's reference line). */
  hrtStartLabel: string | null;
};

export type HrtResponseInput = {
  hrtStartDate: string | null | undefined; // YYYY-MM-DD
  onHRTFull: boolean;
  /** Logs spanning at least pre+post window, dateKey → symptom → severity. */
  logsByDate: Record<string, Record<string, number>>;
};

function addDaysKey(baseKey: string, deltaDays: number): string {
  const d = parseLocalDateKey(baseKey);
  if (!d) return baseKey;
  d.setDate(d.getDate() + deltaDays);
  return localDateKey(d);
}

function buildKeys(startKey: string, fromOffset: number, toOffset: number): string[] {
  // inclusive of both ends
  const out: string[] = [];
  for (let i = fromOffset; i <= toOffset; i++) out.push(addDaysKey(startKey, i));
  return out;
}

export function buildHrtResponse(input: HrtResponseInput): HrtResponse {
  const empty: HrtResponse = {
    state: "hidden",
    daysUntilUnlock: null,
    preDays: null,
    postDays: null,
    rows: null,
    totalImproved: 0,
    totalUnchanged: 0,
    totalWorsened: 0,
    overallPct: 0,
    worstResponder: null,
    topImprover: null,
    chartData: null,
    hrtStartLabel: null,
  };

  // Gate 1: not on HRT or no start date → hidden
  if (!input.onHRTFull) return empty;
  const startD = parseLocalDateKey(input.hrtStartDate);
  if (!startD) return empty;

  const today = new Date(); today.setHours(0, 0, 0, 0);
  // Defensive: future start date → hidden
  if (startD.getTime() > today.getTime()) return empty;

  const startKey = localDateKey(startD);
  const unlockDate = new Date(startD); unlockDate.setDate(unlockDate.getDate() + 30);
  const daysUntilUnlock = Math.ceil((unlockDate.getTime() - today.getTime()) / 86400000);

  // Gate 2: post window not yet 30 days complete → pending
  if (today.getTime() < unlockDate.getTime()) {
    return { ...empty, state: "pending", daysUntilUnlock: Math.max(1, daysUntilUnlock) };
  }

  // Build window date keys (exclude hrtStartDate itself).
  const preKeys = buildKeys(startKey, -30, -1);
  const postKeys = buildKeys(startKey, 1, 30);

  const isLoggedDay = (k: string): boolean => {
    const m = input.logsByDate[k];
    if (!m) return false;
    for (const v of Object.values(m)) {
      if (typeof v === "number" && v >= 0 && v <= 4) return true;
    }
    return false;
  };
  const preLoggedDays = preKeys.filter(isLoggedDay).length;
  const postLoggedDays = postKeys.filter(isLoggedDay).length;

  // Gate 3: data floor — ≥10 logged days each side
  if (preLoggedDays < 10 || postLoggedDays < 10) {
    return { ...empty, state: "data-floor", preDays: preLoggedDays, postDays: postLoggedDays };
  }

  // Gate 4 + table build: per-symptom requires ≥3 logs in BOTH windows.
  const symptomNames = new Set<string>();
  for (const k of [...preKeys, ...postKeys]) {
    const m = input.logsByDate[k];
    if (!m) continue;
    for (const name of Object.keys(m)) symptomNames.add(name);
  }

  const rows: HrtResponseRow[] = [];
  for (const name of symptomNames) {
    const preVals: number[] = [];
    const postVals: number[] = [];
    for (const k of preKeys) {
      const v = input.logsByDate[k]?.[name];
      if (typeof v === "number" && v >= 0 && v <= 4) preVals.push(v);
    }
    for (const k of postKeys) {
      const v = input.logsByDate[k]?.[name];
      if (typeof v === "number" && v >= 0 && v <= 4) postVals.push(v);
    }
    if (preVals.length < 3 || postVals.length < 3) continue;
    const preAvg = preVals.reduce((s, v) => s + v, 0) / preVals.length;
    const postAvg = postVals.reduce((s, v) => s + v, 0) / postVals.length;
    const delta = postAvg - preAvg;
    const direction: HrtResponseRow["direction"] =
      delta <= -0.5 ? "↓" : delta >= 0.5 ? "↑" : "→";
    rows.push({
      symptom: name,
      preAvg,
      postAvg,
      delta,
      preN: preVals.length,
      postN: postVals.length,
      direction,
    });
  }

  rows.sort((a, b) => a.delta - b.delta);

  let improved = 0, unchanged = 0, worsened = 0;
  for (const r of rows) {
    if (r.direction === "↓") improved++;
    else if (r.direction === "↑") worsened++;
    else unchanged++;
  }

  // Overall symptom load on the 1–4 severity scale, summed across all
  // qualifying rows. Sign convention: positive = reduced (improvement).
  let preLoadSum = 0, postLoadSum = 0;
  for (const r of rows) {
    preLoadSum += r.preAvg;
    postLoadSum += r.postAvg;
  }
  const overallPct = preLoadSum > 0
    ? Math.round(((preLoadSum - postLoadSum) / preLoadSum) * 100)
    : 0;

  // Worst responder = row with the LARGEST delta (post − pre), i.e. the
  // last entry once sorted ascending by delta. pct uses the same
  // sign convention as overallPct: positive = improved, negative = worsened.
  let worstResponder: { name: string; pct: number } | null = null;
  if (rows.length > 0) {
    const w = rows[rows.length - 1];
    const wPct = w.preAvg > 0
      ? Math.round(((w.preAvg - w.postAvg) / w.preAvg) * 100)
      : 0;
    worstResponder = { name: w.symptom, pct: wPct };
  }

  // Top improver = row with the SMALLEST delta (most negative post-pre),
  // first entry once sorted ascending. Same sign convention.
  let topImprover: { name: string; pct: number } | null = null;
  if (rows.length > 0) {
    const t = rows[0];
    const tPct = t.preAvg > 0
      ? Math.round(((t.preAvg - t.postAvg) / t.preAvg) * 100)
      : 0;
    topImprover = { name: t.symptom, pct: tPct };
  }

  // Build the 60-day before/after chart series. Only include symptoms that
  // qualified for `rows` so the chart math matches the table.
  const qualifyingSymptoms = rows.map(r => r.symptom);
  const fmtLabel = (k: string): string => {
    const d = parseLocalDateKey(k);
    if (!d) return k;
    return d.toLocaleDateString("en-US", { month: "short", day: "numeric" });
  };
  const dayTotal = (k: string): number => {
    const m = input.logsByDate[k];
    if (!m) return 0;
    let s = 0;
    for (const name of qualifyingSymptoms) {
      const v = m[name];
      if (typeof v === "number" && v >= 0 && v <= 4) s += v;
    }
    return s;
  };
  const series: { date: string; total: number; phase: "before" | "after"; avg: number }[] = [];
  for (const k of preKeys) series.push({ date: fmtLabel(k), total: dayTotal(k), phase: "before", avg: 0 });
  for (const k of postKeys) series.push({ date: fmtLabel(k), total: dayTotal(k), phase: "after", avg: 0 });
  for (let i = 0; i < series.length; i++) {
    const s = Math.max(0, i - 3);
    const e = Math.min(series.length, i + 4);
    const win = series.slice(s, e).map(x => x.total);
    series[i].avg = Number((win.reduce((a, b) => a + b, 0) / win.length).toFixed(1));
  }
  const hrtStartLabel = startD.toLocaleDateString("en-US", { month: "short", day: "numeric" });

  return {
    state: "ready",
    daysUntilUnlock: null,
    preDays: preLoggedDays,
    postDays: postLoggedDays,
    rows,
    totalImproved: improved,
    totalUnchanged: unchanged,
    totalWorsened: worsened,
    overallPct,
    worstResponder,
    topImprover,
    chartData: series,
    hrtStartLabel,
  };
}

// ============================================================
// SECTION E — Mood Summary (last 90 days)
// Scale: 0=Great (best) … 4=Rough (worst). Lower = better.
// ============================================================

export const MOOD_LABELS: Record<number, string> = {
  0: "😄 Great",
  1: "🙂 Good",
  2: "😐 Okay",
  3: "😔 Low",
  4: "😩 Rough",
};

export type MoodDistributionEntry = {
  mood: 0 | 1 | 2 | 3 | 4;
  label: string;
  pct: number;
};

export type MoodSummary = {
  state: "hidden" | "pending" | "empty" | "caveat" | "ready";
  /** Only set when state === 'pending'. Days until milestoneDay reaches 14. */
  daysUntilUnlock: number | null;
  mostCommon: { mood: number; label: string; pct: number } | null;
  distribution: MoodDistributionEntry[] | null;
  /** delta = secondHalfMean − firstHalfMean. Lower = better mood. */
  trend: { direction: "improving" | "stable" | "declining"; delta: number } | null;
  coverage: { logged: number; total: 90; pct: number };
};

export type MoodSummaryInput = {
  milestoneDay: number;
  /** Mood rows from fetchMoodsBetween for the last-90 window. */
  moods: { log_date: string; mood: number }[];
};

export function buildMoodSummary(input: MoodSummaryInput): MoodSummary {
  const empty: MoodSummary = {
    state: "hidden",
    daysUntilUnlock: null,
    mostCommon: null,
    distribution: null,
    trend: null,
    coverage: { logged: 0, total: 90, pct: 0 },
  };

  // Gate 1: milestone < 14 → pending
  if (input.milestoneDay < 14) {
    return {
      ...empty,
      state: "pending",
      daysUntilUnlock: Math.max(1, 14 - input.milestoneDay),
    };
  }

  // Defensive: filter to valid 0-4 ints; one row per day already enforced by DB.
  const valid = (input.moods || []).filter(r => {
    const m = r.mood;
    if (typeof m !== "number" || !Number.isFinite(m)) {
      console.warn("[buildMoodSummary] skipping non-numeric mood", r);
      return false;
    }
    if (m < 0 || m > 4) {
      console.warn("[buildMoodSummary] skipping out-of-range mood", r);
      return false;
    }
    return true;
  });

  const N = valid.length;
  const coverage = { logged: N, total: 90 as const, pct: Math.round((N / 90) * 100) };

  // Gate 2: 0 logs → empty
  if (N === 0) {
    return { ...empty, state: "empty", coverage };
  }

  // Distribution
  const counts = [0, 0, 0, 0, 0];
  for (const r of valid) counts[Math.round(r.mood)]++;
  const rawPct = counts.map(c => (c / N) * 100);
  const rounded = rawPct.map(p => Math.round(p));
  const sum = rounded.reduce((s, v) => s + v, 0);
  if (sum !== 100 && N > 0) {
    // Allocate drift to the largest bucket (ties → lowest mood index = best mood).
    let idx = 0;
    for (let i = 1; i < 5; i++) if (counts[i] > counts[idx]) idx = i;
    rounded[idx] += 100 - sum;
  }
  const distribution: MoodDistributionEntry[] = [0, 1, 2, 3, 4].map(m => ({
    mood: m as 0 | 1 | 2 | 3 | 4,
    label: MOOD_LABELS[m],
    pct: rounded[m],
  }));

  // Most common (ties → lowest mood score = best)
  let topMood = 0;
  for (let i = 1; i < 5; i++) if (counts[i] > counts[topMood]) topMood = i;
  const mostCommon = {
    mood: topMood,
    label: MOOD_LABELS[topMood],
    pct: rounded[topMood],
  };

  // Trend (only when N >= 6, ≥3 each half)
  let trend: MoodSummary["trend"] = null;
  if (N >= 6) {
    const sorted = [...valid].sort((a, b) => a.log_date.localeCompare(b.log_date));
    const half = Math.floor(N / 2); // first half size; odd N → middle goes to second half
    const firstHalf = sorted.slice(0, half);
    const secondHalf = sorted.slice(half);
    if (firstHalf.length >= 3 && secondHalf.length >= 3) {
      const mean = (arr: typeof sorted) =>
        arr.reduce((s, r) => s + r.mood, 0) / arr.length;
      const firstMean = mean(firstHalf);
      const secondMean = mean(secondHalf);
      const delta = secondMean - firstMean;
      const direction: "improving" | "stable" | "declining" =
        delta <= -0.5 ? "improving" : delta >= 0.5 ? "declining" : "stable";
      trend = { direction, delta };
    }
  }

  // Gate 3 vs 4
  const state: MoodSummary["state"] = N < 14 ? "caveat" : "ready";

  return {
    state,
    daysUntilUnlock: null,
    mostCommon,
    distribution,
    trend,
    coverage,
  };
}



// ============================================================
// SECTION — Discussion Points for Your Visit
// 3–5 personalized questions derived from the user's data state.
// Pure function: takes already-built section outputs + raw inputs,
// returns ranked, deduped, capped list. No data fetching here.
// ============================================================

export type DiscussionQuestion = {
  id: number;        // 1..8 — see CANDIDATE POOL in spec
  question: string;  // fully-substituted text, patient voice
  priority: number;  // lower = higher priority
};

export type DiscussionInput = {
  user: {
    hrtType?: string | null;
    hrtDose?: string | null;
    hrtStartDate?: string | null;
  } | null;
  onHRTFull: boolean;
  hrtResponse: HrtResponse;          // Section C
  directions: DirectionRow[];        // Section A3 (real)
  directionWindowReady: boolean;     // milestone≥60 + ≥10 logs each window
  labsCount: number;
  cycleData: any[];                  // raw cycle list (oldest or newest order tolerated)
  logsByDate: Record<string, Record<string, number>>;
};

function humanizeSinceStart(startDate?: string | null): string {
  const d = parseLocalDateKey(startDate);
  if (!d) return "some time";
  const today = new Date(); today.setHours(0,0,0,0);
  const days = Math.floor((today.getTime() - d.getTime()) / 86400000);
  if (days < 0) return "some time";
  if (days < 14) return `${Math.max(1, days)} day${days === 1 ? "" : "s"}`;
  if (days < 60) {
    const w = Math.round(days / 7);
    return `${w} week${w === 1 ? "" : "s"}`;
  }
  const months = Math.round(days / 30);
  if (months < 12) return `${months} month${months === 1 ? "" : "s"}`;
  const years = Math.floor(months / 12);
  const remM = months % 12;
  if (remM === 0) return `${years} year${years === 1 ? "" : "s"}`;
  return `${years}y ${remM}m`;
}

function avgLast30(logsByDate: Record<string, Record<string, number>>, symptom: string): { avg: number; n: number } {
  let sum = 0, n = 0;
  for (let i = 0; i < 30; i++) {
    const d = new Date(); d.setDate(d.getDate() - i);
    const k = localDateKey(d);
    const v = logsByDate[k]?.[symptom];
    if (typeof v === "number") { sum += v; n++; }
  }
  return { avg: n ? sum / n : 0, n };
}

export function buildDoctorQuestions(input: DiscussionInput): DiscussionQuestion[] {
  const out: DiscussionQuestion[] = [];
  const u = input.user || {};
  const hrtRows = input.hrtResponse?.rows || [];
  const hrtReady = input.hrtResponse?.state === "ready";

  // 1. HRT dose effectiveness — ≥3 symptoms with Δ > -0.5 (NOT meaningfully improved)
  if (input.onHRTFull && hrtReady) {
    const notImproved = hrtRows.filter(r => r.delta > -0.5);
    if (notImproved.length >= 3) {
      const dosePart = [u.hrtType, u.hrtDose].filter(Boolean).join(" ").trim() || "my HRT";
      const time = humanizeSinceStart(u.hrtStartDate);
      out.push({
        id: 1, priority: 1,
        question: `Since starting ${dosePart} about ${time} ago, ${notImproved.length} of my tracked symptoms haven't improved much. Is the current dose right for me, or could we try adjusting it?`,
      });
    }
  }

  // 2. Different delivery method — ≥1 symptom worsened (Δ ≥ +0.5)
  if (input.onHRTFull && hrtReady) {
    const worsened = hrtRows.filter(r => r.delta >= 0.5).sort((a, b) => b.delta - a.delta);
    if (worsened[0]) {
      out.push({
        id: 2, priority: 2,
        question: `${worsened[0].symptom} severity has actually worsened since I started HRT. Could a different delivery method (patch, oral, cream, gel) work better for me?`,
      });
    }
  }

  // 3. Revisit HRT plan — ≥3 symptoms NOT improving in Section A direction window
  if (input.onHRTFull && input.directionWindowReady) {
    const notImprovingDirs = input.directions.filter(d => d.dir !== "improving");
    if (notImprovingDirs.length >= 3) {
      const time = humanizeSinceStart(u.hrtStartDate);
      out.push({
        id: 3, priority: 3,
        question: `Despite being on HRT for ${time}, several symptoms aren't improving. Should we revisit the plan?`,
      });
    }
  }

  // 5. Bleeding pattern — ≥2 completed cycles AND (variability ≥14d OR ≥1 outside 21–35)
  const completed = (input.cycleData || []).filter((c: any) => typeof c?.length === "number" && !c?.inProgress);
  if (completed.length >= 2) {
    const lengths = completed.map((c: any) => c.length as number);
    const variability = Math.max(...lengths) - Math.min(...lengths);
    const anyOutside = lengths.some(l => l < 21 || l > 35);
    if (variability >= 14 || anyOutside) {
      out.push({
        id: 5, priority: 4,
        question: `My cycle length has varied notably over my last ${completed.length} cycles. Is this pattern worth monitoring more closely?`,
      });
    }
  }

  // 6. Brain fog causes — avg severity last 30 days ≥ 2.5
  {
    const bf = avgLast30(input.logsByDate, "Brain fog");
    if (bf.n > 0 && bf.avg >= 2.5) {
      out.push({
        id: 6, priority: 5,
        question: `I've been experiencing brain fog at moderate-to-severe levels lately. Could it have causes beyond menopause — like sleep, B12, or thyroid?`,
      });
    }
  }

  // 6b. Persistent symptom (other than brain fog) — avg severity ≥ 2.5 last 30d.
  {
    const candidates = ["Night sweats", "Poor sleep", "Mood swings", "Anxiety", "Fatigue", "Joint pain", "Headaches"];
    let worst: { name: string; avg: number } | null = null;
    for (const name of candidates) {
      const a = avgLast30(input.logsByDate, name);
      if (a.n >= 5 && a.avg >= 2.5 && (!worst || a.avg > worst.avg)) worst = { name, avg: a.avg };
    }
    if (worst) {
      out.push({
        id: 9, priority: 5,
        question: `${worst.name} has been running at moderate-to-severe levels (avg ${worst.avg.toFixed(1)}/4) over the last month. What targeted options should we consider beyond what I'm already doing?`,
      });
    }
  }

  // 6c. Estradiol trend on HRT — rising or holding low across ≥2 panels.
  if (input.onHRTFull && input.labsCount >= 2) {
    out.push({
      id: 10, priority: 6,
      question: `Looking at my estradiol trend across the panels I've uploaded, does my current level look right for symptom relief, or should we re-check timing or dose?`,
    });
  }

  // 4. Bloodwork — 0 lab results uploaded
  if (input.labsCount === 0) {
    out.push({
      id: 4, priority: 7,
      question: `I haven't had bloodwork run recently. Should I get tested for thyroid (TSH, Free T4), cortisol, or Vitamin D to rule out other contributors to my symptoms?`,
    });
  }

  const derived = out.slice().sort((a, b) => a.priority - b.priority);

  // Defaults (always candidates).
  const defaults: DiscussionQuestion[] = [
    { id: 7, priority: 7, question: `Are there lifestyle factors (stress, alcohol, caffeine, sleep) you'd recommend tracking alongside my symptoms to identify triggers?` },
    { id: 8, priority: 8, question: `How often should I follow up with you to monitor my hormone levels and treatment effectiveness?` },
  ];

  // Selection: max 5; prefer derived; fill with defaults to reach 4–5 when few derived.
  let final: DiscussionQuestion[] = [];
  if (derived.length >= 5) {
    final = derived.slice(0, 5);
  } else if (derived.length >= 3) {
    const need = Math.min(2, 5 - derived.length);
    final = [...derived, ...defaults.slice(0, need)];
  } else {
    final = [...derived, ...defaults];
    final = final.slice(0, 5);
  }
  return final;
}


// ============================================================
// SECTION — Data Confidence Score
// Composite 0–100 trust signal that always renders on the report.
// ============================================================

export type DataConfidence = {
  score: number;
  label: "HIGH" | "MODERATE" | "LOW";
  coverage: number;
  consistency: number;
  completeness: number;
  recency: number;
  breakdown: {
    loggedDays: number;
    totalDays: 90;
    checkinPct: number;
    hrtLogPct: number | null;
  };
};

export type DataConfidenceInput = {
  onHRTFull: boolean;
  /** dateKey → symptom → severity, last 90 days. */
  logsByDate: Record<string, Record<string, number>>;
  /** Distinct dateKeys with a mood row in last 90 days. */
  moodDates: string[];
  /** Distinct dateKeys with a daily_hrt row in last 90 days. */
  hrtDates: string[];
  /**
   * Days since the user started tracking (canonical "Day N" / milestoneDay
   * from computeReportData — earliest of first daily_log or signupDate).
   * Used to scale the coverage/consistency denominators so diligent new
   * users aren't punished by a fixed 90-day window. Defaults to 90 when
   * absent (legacy callers) so behavior is unchanged.
   */
  daysSinceTracking?: number;
};

export function buildDataConfidence(input: DataConfidenceInput): DataConfidence {
  // Tunable maturity knobs.
  const MIN_WINDOW = 14;              // never divide by less than 14 days
  const MAX_WINDOW = 90;              // never look back further than 90 days
  const MATURITY_DAYS = 30;           // < this many days tracked → cap score
  const SHORT_HISTORY_CAP = 79;       // max score allowed before maturity

  const daysSinceTracking = Math.max(1, Math.floor(input.daysSinceTracking ?? MAX_WINDOW));
  const effectiveWindow = Math.min(MAX_WINDOW, Math.max(MIN_WINDOW, daysSinceTracking));

  // Build the 90 local-TZ date keys (oldest → newest = today).
  const last90Keys: string[] = [];
  for (let i = 89; i >= 0; i--) {
    const d = new Date(); d.setDate(d.getDate() - i);
    last90Keys.push(localDateKey(d));
  }
  const last90Set = new Set(last90Keys);

  const symDates = new Set<string>(Object.keys(input.logsByDate || {}).filter(k => last90Set.has(k)));
  const moodSet = new Set<string>((input.moodDates || []).filter(k => last90Set.has(k)));
  const hrtSet  = new Set<string>((input.hrtDates  || []).filter(k => last90Set.has(k)));

  // Union: any-type log day.
  const anyLogSet = new Set<string>();
  for (const k of symDates) anyLogSet.add(k);
  for (const k of moodSet)  anyLogSet.add(k);
  for (const k of hrtSet)   anyLogSet.add(k);

  const loggedDays = anyLogSet.size;

  // 1. Coverage (40%) — scaled to the user's actual tracking window.
  const coverage = Math.min((loggedDays / effectiveWindow) * 100, 100);

  // 2. Consistency (30%) — rolling 7-day windows (≥5 logged) across the
  //    effective window, not a fixed 90. Slide over the most recent
  //    `effectiveWindow` days; total window count = max(effectiveWindow - 6, 1).
  const windowDays = last90Keys.slice(-effectiveWindow);
  const windowCount = Math.max(effectiveWindow - 6, 1);
  let qualifyingWindows = 0;
  const maxStart = Math.max(0, windowDays.length - 7);
  for (let start = 0; start <= maxStart; start++) {
    let c = 0;
    for (let j = 0; j < 7; j++) if (anyLogSet.has(windowDays[start + j])) c++;
    if (c >= 5) qualifyingWindows++;
  }
  const consistency = Math.min((qualifyingWindows / windowCount) * 100, 100);

  // 3. Completeness (20%) — unchanged.
  let completeDays = 0;
  for (const k of anyLogSet) {
    const hasSym = symDates.has(k);
    const hasMood = moodSet.has(k);
    const hasHrt = hrtSet.has(k);
    const isComplete = input.onHRTFull
      ? (hasSym && hasMood && hasHrt)
      : (hasSym && hasMood);
    if (isComplete) completeDays++;
  }
  const completeness = loggedDays > 0 ? (completeDays / loggedDays) * 100 : 0;

  // 4. Recency (10%) — unchanged.
  const last7 = last90Keys.slice(-7);
  const recency = last7.some(k => anyLogSet.has(k)) ? 100 : 0;

  const composite = (0.4 * coverage) + (0.3 * consistency) + (0.2 * completeness) + (0.1 * recency);
  let score = Math.round(composite);
  // Maturity ceiling: short history can read MODERATE but never HIGH.
  if (daysSinceTracking < MATURITY_DAYS) {
    score = Math.min(score, SHORT_HISTORY_CAP);
  }
  const label: DataConfidence["label"] = score >= 80 ? "HIGH" : score >= 50 ? "MODERATE" : "LOW";

  const checkinPct = Math.round((loggedDays / effectiveWindow) * 100);
  const hrtLogPct = input.onHRTFull ? Math.round((hrtSet.size / 90) * 100) : null;

  return {
    score,
    label,
    coverage: Math.round(coverage),
    consistency: Math.round(consistency),
    completeness: Math.round(completeness),
    recency,
    breakdown: { loggedDays, totalDays: 90, checkinPct, hrtLogPct },
  };
}


// ============================================================
// SECTION — Hot flash event direction + summary (Turn 2e)
// Event-tracked symptoms can't be averaged on the severity scale,
// so we compare COUNTS between last-30d and prior-30d windows and
// summarize time-of-day distribution for the clinician.
// ============================================================

export type EventDirectionRow = {
  name: "Hot flashes";
  lastEvents: number;
  priorEvents: number;
  lastDays: number;   // days in window with ≥1 event
  priorDays: number;
  /** Integer % change in event COUNT (NaN-safe). null when undefined. */
  pct: number | null;
  /**
   * "improving"  : last < prior (>15% drop)
   * "worsening"  : last > prior (>15% rise)
   * "stable"     : within ±15%
   * "new"        : prior = 0, last > 0
   * "resolved"   : prior > 0, last = 0
   * "none"       : prior = 0 AND last = 0 — row should be hidden
   */
  dir: "improving" | "worsening" | "stable" | "new" | "resolved" | "none";
  peakHourBand: string | null;
};

export type HotFlashEvent = { occurred_at: string };

export type HotFlashSummary = {
  /** Total events in the window. */
  total: number;
  /** Distinct local-TZ days with ≥1 event. */
  daysWithEvent: number;
  /** Median events per logged day (0 when no logged days). */
  medianPerDay: number;
  /** 4-hour band with the most events; null when total === 0. */
  peakHourBand: string | null;
  /** 24-bin histogram (hour-of-day 0..23) for the sparkline. */
  hourBins: number[];
};

const HOUR_BAND_LABELS: Record<number, string> = {
  0: "Night (12–4am)",
  1: "Early morning (4–8am)",
  2: "Morning (8am–12pm)",
  3: "Afternoon (12–4pm)",
  4: "Evening (4–8pm)",
  5: "Late evening (8pm–12am)",
};

function eventLocalDay(ev: HotFlashEvent): string {
  return localDateKey(new Date(ev.occurred_at));
}

export function summarizeHotFlashEvents(events: HotFlashEvent[]): HotFlashSummary {
  const evs = events || [];
  const dayCounts = new Map<string, number>();
  const hourBins = new Array(24).fill(0);
  const bandCounts = new Array(6).fill(0);
  for (const ev of evs) {
    const dk = eventLocalDay(ev);
    dayCounts.set(dk, (dayCounts.get(dk) || 0) + 1);
    const d = new Date(ev.occurred_at);
    const h = d.getHours();
    hourBins[h]++;
    bandCounts[Math.floor(h / 4)]++;
  }
  const total = evs.length;
  const daysWithEvent = dayCounts.size;
  // Median events per logged day.
  let medianPerDay = 0;
  if (daysWithEvent > 0) {
    const arr = [...dayCounts.values()].sort((a, b) => a - b);
    const mid = Math.floor(arr.length / 2);
    medianPerDay = arr.length % 2 === 0
      ? (arr[mid - 1] + arr[mid]) / 2
      : arr[mid];
  }
  let peakHourBand: string | null = null;
  if (total > 0) {
    let topIdx = 0;
    for (let i = 1; i < 6; i++) if (bandCounts[i] > bandCounts[topIdx]) topIdx = i;
    peakHourBand = HOUR_BAND_LABELS[topIdx];
  }
  return { total, daysWithEvent, medianPerDay, peakHourBand, hourBins };
}

/**
 * Build the per-event Direction row for Hot flashes. NaN-safe:
 *   - prior=0, last=0 → dir="none" (caller hides the row)
 *   - prior=0, last>0 → dir="new", pct=null
 *   - prior>0, last=0 → dir="resolved", pct=-100
 *   - else            → pct = round((last-prior)/prior * 100)
 */
export function buildHotFlashEventDirection(args: {
  lastEvents: number;
  priorEvents: number;
  lastDays: number;
  priorDays: number;
  peakHourBand: string | null;
}): EventDirectionRow {
  const { lastEvents, priorEvents, lastDays, priorDays, peakHourBand } = args;
  let pct: number | null = 0;
  let dir: EventDirectionRow["dir"] = "stable";
  if (priorEvents === 0 && lastEvents === 0) {
    pct = null;
    dir = "none";
  } else if (priorEvents === 0 && lastEvents > 0) {
    pct = null;
    dir = "new";
  } else if (priorEvents > 0 && lastEvents === 0) {
    pct = -100;
    dir = "resolved";
  } else {
    pct = Math.round(((lastEvents - priorEvents) / priorEvents) * 100);
    dir = pct < -15 ? "improving" : pct > 15 ? "worsening" : "stable";
  }
  return { name: "Hot flashes", lastEvents, priorEvents, lastDays, priorDays, pct, dir, peakHourBand };
}


// ============================================================
// Shared 90-day symptom heatmap renderer.
//
// Single source of truth for the heatmap SVG used in BOTH the peri
// Symptom Intelligence Report AND the postmeno Vitality Report.
// Data-driven symptom universe: Hot flashes (event-tracked) always first,
// then the severity-scored symptoms the patient actually logged in the
// last 90 days. Per-day shading is delegated to heatCellColor() — same
// buckets MenopauseScoreDetail's heat row uses.
//
// Extracted verbatim from KnowMenoApp.tsx so the peri report's rendered
// output stays byte-identical after the extraction.
// ============================================================
import { heatCellColor } from "./menoScore";

const HEATMAP_COLOR_MAP: Record<string, string> = {
  "Hot flashes": "#FF5C7A",
  "Night sweats": "#D78B2E",
  "Poor sleep": "#FF5C7A",
  "Brain fog": "#FFC1CD",
  "Mood swings": "#FF5C7A",
};

export function renderSymptomHeatmapSvg(args: {
  logsByDate: Record<string, Record<string, number>>;
  hfCountByDate: Record<string, number>;
  days: number;
}): string {
  const { logsByDate, hfCountByDate, days } = args;
  const d = Math.max(1, Math.min(days, 90));
  const heatLoggedNames = (() => {
    const s = new Set<string>();
    for (const dk of Object.keys(logsByDate || {})) {
      for (const name of Object.keys(logsByDate[dk] || {})) {
        if (name !== "Hot flashes" && typeof logsByDate[dk][name] === "number") s.add(name);
      }
    }
    return [...s].sort();
  })();
  const HEAT_SYMPTOMS: { name: string; color: string; key: string }[] = [
    { name: "Hot flashes", color: HEATMAP_COLOR_MAP["Hot flashes"], key: "Hot flashes" },
    ...heatLoggedNames.map(n => ({ name: n, key: n, color: HEATMAP_COLOR_MAP[n] || "#8E5A8F" })),
  ];
  const heatDateKeys = (() => {
    const keys: string[] = [];
    for (let i = d - 1; i >= 0; i--) {
      const dt = new Date();
      dt.setDate(dt.getDate() - i);
      keys.push(localDateKey(dt));
    }
    return keys;
  })();
  const rowH = 16, labelW = 90, w = 680, cellW = (w - labelW) / d;
  const rows = HEAT_SYMPTOMS.map((s, r) => {
    const cells = heatDateKeys.map((dk, i) => {
      const col = heatCellColor({
        symptomKey: s.key,
        severity: logsByDate[dk]?.[s.key] ?? null,
        eventCount: s.key === "Hot flashes" ? (hfCountByDate[dk] || 0) : 0,
        baseColor: s.color,
      });
      return `<rect x="${labelW + i * cellW}" y="${r * (rowH + 2)}" width="${cellW - 0.5}" height="${rowH}" fill="${col}" rx="1"/>`;
    }).join("");
    const text = `<text x="0" y="${r * (rowH + 2) + 11}" font-size="11" fill="#4B1F4F">${s.name}</text>`;
    return text + cells;
  }).join("");
  return `<svg viewBox="0 0 ${w} ${HEAT_SYMPTOMS.length * (rowH + 2)}" width="100%" style="display:block;background:#fff;border:0.5px solid #E8D5DA;border-radius:6px;padding:6px;box-sizing:border-box;">${rows}</svg>`;
}
